import React, { useMemo, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Import Ionic
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonRow,
  IonIcon,
  useIonAlert,
  IonInput,
  IonCardSubtitle,
} from "@ionic/react";
import { add, remove, trash } from "ionicons/icons";

// Import Styles
import styles from "./ProductCard.module.scss";
import classNames from "classnames/bind";

import { Image } from "../../../../../components/Image/Image";
import { Product as TProduct } from "@core/types";
import { CartDataProductsItem, useCartData } from "../../../../../providers/cartDataStore";
import { AttributeBadge } from "../../../../../fsd/shared/AttributeBadge/AttributeBadge";
import { useConfig } from "../../../../../providers/config";
import { getPreviewImage } from "../../../../../utils/getPreviewImage";
import { safeEncodeURIComponent } from "../../../../../utils/urlEncoding";
import { useAuth } from "../../../../../contexts/auth-context";
import { useProductDetailCounter } from "../../../../../hooks/useProductDetailCounter";

interface CartProductCardProps {
  product: TProduct;
  cartItem: CartDataProductsItem;
  showCounter?: boolean;
  className?: string;
}

const cx = classNames.bind(styles);

// Константы для адаптивной ширины
const INPUT_WIDTHS = {
  COMPACT: "30px",
  EXPANDED: "35px",
  EXPAND_THRESHOLD: 4,
} as const;

// Константы для алерта
const ALERT_CONFIG = {
  HEADER: "Вы точно хотите удалить этот товар из корзины?",
  MESSAGE: "Данное действие нельзя будет отменить.",
  CANCEL_TEXT: "Отмена",
  CONFIRM_TEXT: "OK",
} as const;

const CartProductCard: React.FC<CartProductCardProps> = ({ product, cartItem, showCounter = true, className = "" }) => {
  const location = useLocation();
  const { isUserAuthenticated } = useAuth();
  const { ignoredProductAttributes, showParentName } = useConfig();
  const { addToCart, removeFromCart, removeAllFromCart, removeSoldOutProduct, setProductQuantity } = useCartData();
  const config = useConfig();

  const [presentAlert] = useIonAlert();
  const [inputValue, setInputValue] = useState<number>(cartItem.quantity);

  // Get base price for the counter hook
  const getBasePrice = () => {
    const isDiscount =
      product?.discountPrice &&
      product?.discountPrice !== "0" &&
      product?.discountPrice !== "0.00" &&
      product?.discountPrice !== product?.price;

    const basePrice = isDiscount ? product.discountPrice! : product.price;
    return Number(basePrice) || 0;
  };

  // Use the same counter hook as ProductDetailFortime
  const counterHook = useProductDetailCounter({
    product,
    selectedVariation: product.type === "variable" ? product : null,
    basePrice: getBasePrice(),
    cartQuantity: cartItem.quantity,
  });

  useEffect(() => {
    if (
      cartItem.selectedAttribute &&
      (!counterHook.selectedAttribute || cartItem.selectedAttribute.id !== counterHook.selectedAttribute.id)
    ) {
      counterHook.handleAttributeSelect(cartItem.selectedAttribute);
    }
  }, [cartItem.selectedAttribute, counterHook.selectedAttribute, counterHook]);

  const hasAttributes = !!cartItem.attributes?.length;
  const showOriginalQuantity = config.isUnavailableProductsEnabled && cartItem.originalQuantity > cartItem.quantity;

  const linkToVariationProduct = useMemo(() => {
    let link = `/product-detail/${safeEncodeURIComponent(cartItem.parentId)}`;

    if (cartItem.parentId !== cartItem.id && cartItem.attributes?.length) {
      const queryParams = cartItem.attributes
        ?.map(({ option, name }) => {
          return `${safeEncodeURIComponent(name)}=${safeEncodeURIComponent(option ?? "")}`;
        })
        .join("&");

      link += `?${queryParams}`;
    }

    return link;
  }, [cartItem]);

  const attributes = useMemo(() => {
    if (!cartItem.attributes) {
      return null;
    }

    const attrs = ignoredProductAttributes
      ? cartItem.attributes.filter(
          (attr) =>
            !ignoredProductAttributes.some((ignoredAttr) => ignoredAttr.toLowerCase() === attr.name.toLowerCase()),
        )
      : cartItem.attributes;

    return attrs.map((attr) => {
      return (
        <div key={attr.id} className="flex items-center content-center pl-1 sm:mr-2">
          <div className="pr-1 sm:pr-2">{attr.name}:</div>
          <AttributeBadge value={attr.option ?? ""} isColorPrimary />
        </div>
      );
    });
  }, [cartItem, ignoredProductAttributes]);

  const productImage = getPreviewImage(product.images[0]);

  // Sync input value with cartItem.quantity
  useEffect(() => {
    setInputValue(cartItem.quantity);
  }, [cartItem.quantity]);

  const setAdaptiveWidth = (inputElement: any, length: number) => {
    if (inputElement) {
      const width = length >= INPUT_WIDTHS.EXPAND_THRESHOLD ? INPUT_WIDTHS.EXPANDED : INPUT_WIDTHS.COMPACT;
      inputElement.style.width = width;
    }
  };

  const handleQuantityInputBlur = () => {
    let numericValue = Number(String(inputValue));
    const current = Number(cartItem.quantity);

    if (isNaN(numericValue) || numericValue < 1) numericValue = 1;
    if (numericValue === 0) numericValue = 1;
    if (numericValue > cartItem.stockQuantity) numericValue = cartItem.stockQuantity;

    setInputValue(numericValue);

    if (numericValue !== current) {
      const updatedProduct = {
        ...product,
        selectedAttribute: counterHook.selectedAttribute || cartItem.selectedAttribute,
      };

      setProductQuantity(cartItem.id, numericValue, updatedProduct as any, isUserAuthenticated);
    }
  };

  const handleDeleteAlert = () => {
    presentAlert({
      header: ALERT_CONFIG.HEADER,
      message: ALERT_CONFIG.MESSAGE,
      buttons: [
        {
          text: ALERT_CONFIG.CANCEL_TEXT,
          role: "cancel",
          handler: () => {},
        },
        {
          text: ALERT_CONFIG.CONFIRM_TEXT,
          role: "confirm",
          handler: () => {
            removeAllFromCart(cartItem.id, isUserAuthenticated);
            showOriginalQuantity && removeSoldOutProduct(cartItem.id);
          },
        },
      ],
    });
  };

  const handleRemoveOne = () => {
    removeFromCart(cartItem.id, isUserAuthenticated);
    showOriginalQuantity && removeSoldOutProduct(cartItem.id);
  };

  const handleQuantityInputChange = (e: any) => {
    let value = e.detail.value ?? "";

    if (value.length > INPUT_WIDTHS.EXPAND_THRESHOLD) {
      value = value.slice(0, INPUT_WIDTHS.EXPAND_THRESHOLD);
      e.target.value = value;
    }

    if (value === "") {
      setInputValue(1);
    } else {
      const numericValue = parseInt(value, 10);
      if (!isNaN(numericValue)) {
        setInputValue(numericValue);
      }
    }

    setAdaptiveWidth(e.target, value.length);
  };

  const handleIncrement = () => {
    const productToAdd = {
      ...product,
      sku: product.sku ?? "",
      weight: product.weight ?? "0",
      portionUnit: product.portionUnit ?? "",
      selectedAttribute: counterHook.selectedAttribute
        ? {
            id: counterHook.selectedAttribute.id,
            name: counterHook.selectedAttribute.name,
            coefficient: counterHook.selectedAttribute.coefficient,
          }
        : cartItem.selectedAttribute,
    };

    addToCart(productToAdd as any);
  };

  const remainingStock = product.stockQuantity - Number(cartItem.quantity);
  const isProductStockLow =
    config.showStockQuantity &&
    config.minLowStockThresholdToShowNumber &&
    product.stockQuantity &&
    (remainingStock <= config.minLowStockThresholdToShowNumber ||
      config.lowStockThresholdToShowPercent >= 100 - (Number(cartItem.quantity) / product.stockQuantity) * 100);

  const stockStatus =
    remainingStock > 0
      ? isProductStockLow && `Доступно ${Math.floor(remainingStock)} ${product.portionUnit ?? ""}`
      : "Количество ограничено";

  return (
    <Link className="flex flex-col" to={linkToVariationProduct}>
      <IonCard
        className={cx(
          "productCard",
          "ion-no-padding",
          className,
          config.showStockStatus && !location.pathname.includes("order") && "!mb-2",
        )}
      >
        <IonCardContent className="ion-no-padding">
          <IonGrid className={cx("productContent", config.showStockStatus && "flex flex-col items-end")}>
            <IonRow className={cx("productContentRow")}>
              <IonCol className={cx("productContentImg")} size={"3"} sizeMd={"2"}>
                <Image image={productImage} />
              </IonCol>
              <IonCol className={cx("productContentDesc")}>
                <IonRow>
                  <IonCol class="ion-text-left">
                    {showParentName && cartItem.parentName ? (
                      <div className="flex flex-col gap-1">
                        <div className={cx("productContentDesc__title-parent")}>{cartItem.parentName}</div>
                        <div className={cx("productContentDesc__title")}>{cartItem.name}</div>
                      </div>
                    ) : (
                      <div className={cx("productContentDesc__title")}>{cartItem.name}</div>
                    )}
                  </IonCol>
                </IonRow>
                {hasAttributes && <IonRow>{attributes}</IonRow>}
                <IonRow className={cx("ion-align-items-center")}>
                  <IonCol class="ion-text-left" size="6">
                    <span>
                      <p className={cx("productCardPrice", "ellipsis !text-base ")}>
                        {cartItem.price}₽/{cartItem.unitName || counterHook.selectedAttribute?.name || "шт."}
                      </p>
                    </span>
                  </IonCol>
                  {showCounter && (
                    <IonCol class="ion-text-right ion-no-padding" size="6">
                      <IonRow class={cx("ion-float-right", styles.buttons)} onClick={(e) => e.preventDefault()}>
                        {showOriginalQuantity && (
                          <div className="mr-2 line-through font-bold text-[--ion-color-danger-shade]">
                            {cartItem.originalQuantity}
                          </div>
                        )}
                        <>
                          <IonButton
                            id="present-alert"
                            fill="outline"
                            size="small"
                            className="compact-button trash-button"
                            onClick={handleDeleteAlert}
                          >
                            <IonIcon size="small" src={trash} />
                          </IonButton>
                        </>

                        <IonButton
                          fill="outline"
                          size="small"
                          color="secondary"
                          className="compact-button"
                          onClick={handleRemoveOne}
                        >
                          <IonIcon size="small" src={remove} />
                        </IonButton>
                        <IonInput
                          min={1}
                          type="number"
                          maxlength={4}
                          pattern="[0-9]*"
                          max={cartItem.stockQuantity}
                          value={inputValue}
                          inputMode="numeric"
                          onBlur={handleQuantityInputBlur}
                          onIonChange={handleQuantityInputChange}
                          onKeyDown={(e) => {
                            // Предотвращаем ввод больше 4 символов
                            const target = e.target as HTMLInputElement;
                            const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];

                            if (target.value.length >= INPUT_WIDTHS.EXPAND_THRESHOLD && !allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className="quantity-input"
                        />
                        <IonButton
                          fill="outline"
                          size="small"
                          color="secondary"
                          className="compact-button"
                          onClick={handleIncrement}
                          disabled={cartItem.quantity + 1 > cartItem.stockQuantity}
                        >
                          <IonIcon size="small" src={add} />
                        </IonButton>
                      </IonRow>
                    </IonCol>
                  )}
                </IonRow>
              </IonCol>
            </IonRow>
            {config.showStockStatus && !location.pathname.includes("order") && (
              <div className={cx("text-sm text-gray-500 mx-1.5 invisible", stockStatus && "!visible")}>
                {stockStatus}
              </div>
            )}
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </Link>
  );
};

export default CartProductCard;
